import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Spin } from 'antd';
import api from '../services/api';

const GOLD = '#B8892A';
const GOLD_L = '#D4A843';
const MUTED = '#6B5E4E';

export default function ShareCard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get('ref') || '';
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [loading, setLoading] = useState(true);
  const [codeInfo, setCodeInfo] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!refCode) {
      setLoading(false);
      setError(true);
      return;
    }
    api.get(`/referral/validate/${encodeURIComponent(refCode)}`)
      .then(res => {
        setCodeInfo(res.data);
      })
      .catch(() => {
        setError(true);
      })
      .finally(() => setLoading(false));
  }, [refCode]);

  const handleRegister = () => {
    navigate(`/register?ref=${encodeURIComponent(refCode)}`);
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'linear-gradient(180deg, #1A1A1A 0%, #2E2418 60%, #1A1A1A 100%)',
      }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #1A1A1A 0%, #2E2418 50%, #1A1A1A 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: isMobile ? '40px 16px' : '60px 20px',
    }}>
      <div style={{
        width: '100%', maxWidth: 400, textAlign: 'center',
      }}>
        {/* Decorative top accent */}
        <div style={{
          width: 60, height: 2, background: `linear-gradient(90deg, transparent, ${GOLD_L}, transparent)`,
          margin: '0 auto 24px',
        }} />

        {/* Brand Logo */}
        <div style={{
          fontSize: isMobile ? 36 : 44, fontWeight: 800, color: GOLD_L,
          fontFamily: 'var(--serif)', marginBottom: 4, letterSpacing: 2,
        }}>
          {t('brand.name')}
        </div>
        <div style={{
          fontSize: 11, color: 'rgba(255,255,255,0.4)', letterSpacing: 4,
          textTransform: 'uppercase', marginBottom: 32,
        }}>
          YUNCHUAN
        </div>

        {/* Main Card */}
        <div style={{
          background: 'rgba(255,255,255,0.05)',
          border: `1px solid rgba(184,137,42,0.2)`,
          borderRadius: 16, padding: isMobile ? '32px 20px' : '40px 32px',
          backdropFilter: 'blur(10px)',
        }}>
          {error ? (
            <>
              <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
              <div style={{ fontSize: 18, fontWeight: 600, color: '#fff', marginBottom: 8 }}>
                {t('referral.shareInvalidTitle')}
              </div>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', lineHeight: 1.6, marginBottom: 24 }}>
                {t('referral.shareInvalidDesc')}
              </div>
              <button onClick={() => navigate('/register')} style={{
                background: `linear-gradient(135deg, ${GOLD}, ${GOLD_L})`,
                color: '#fff', border: 'none', borderRadius: 8,
                padding: '12px 36px', fontSize: 15, fontWeight: 600, cursor: 'pointer',
              }}>{t('referral.goRegister')}</button>
            </>
          ) : (
            <>
              {/* Invitation message */}
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', marginBottom: 8 }}>
                {t('referral.shareInviteMsg')}
              </div>
              <div style={{
                fontSize: isMobile ? 20 : 24, fontWeight: 700, color: '#fff',
                fontFamily: 'var(--serif)', lineHeight: 1.4, marginBottom: 20,
              }}>
                {t('referral.shareHeadline')}
              </div>

              {/* Divider */}
              <div style={{
                width: 40, height: 1, background: `rgba(184,137,42,0.4)`,
                margin: '0 auto 20px',
              }} />

              {/* Platform benefits */}
              <div style={{ marginBottom: 24 }}>
                {[
                  { icon: '🏛️', key: 'shareBenefit1' },
                  { icon: '🔍', key: 'shareBenefit2' },
                  { icon: '🛡️', key: 'shareBenefit3' },
                ].map(b => (
                  <div key={b.key} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '8px 0', textAlign: 'left',
                  }}>
                    <span style={{ fontSize: 18 }}>{b.icon}</span>
                    <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>
                      {t(`referral.${b.key}`)}
                    </span>
                  </div>
                ))}
              </div>

              {/* QR Code Placeholder */}
              <div style={{
                width: 100, height: 100, margin: '0 auto 16px',
                background: '#fff', borderRadius: 8,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexDirection: 'column', gap: 2,
              }}>
                <div style={{ fontSize: 28 }}>📱</div>
                <div style={{ fontSize: 8, color: MUTED }}>QR Code</div>
              </div>

              {/* Referral Code */}
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginBottom: 4 }}>
                {t('referral.shareCodeLabel')}
              </div>
              <div style={{
                display: 'inline-block',
                background: 'rgba(184,137,42,0.15)', border: `1px solid rgba(184,137,42,0.3)`,
                borderRadius: 8, padding: '6px 20px',
                fontSize: 22, fontWeight: 700, color: GOLD_L,
                fontFamily: 'var(--mono)', letterSpacing: 3, marginBottom: 24,
              }}>{refCode}</div>

              {/* CTA Button */}
              <div>
                <button onClick={handleRegister} style={{
                  width: '100%', maxWidth: 280,
                  background: `linear-gradient(135deg, ${GOLD}, ${GOLD_L})`,
                  color: '#fff', border: 'none', borderRadius: 10,
                  padding: '14px 0', fontSize: 16, fontWeight: 700, cursor: 'pointer',
                  boxShadow: '0 4px 16px rgba(184,137,42,0.3)',
                  fontFamily: 'var(--sans)',
                }}>{t('referral.shareRegisterCTA')}</button>
              </div>

              {/* Referrer info */}
              {codeInfo?.referrer_name && (
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginTop: 12 }}>
                  {t('referral.shareInvitedBy', { name: codeInfo.referrer_name })}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{
          marginTop: 32, fontSize: 11, color: 'rgba(255,255,255,0.25)', lineHeight: 1.6,
        }}>
          {t('referral.shareFooter')}
        </div>

        {/* Bottom accent */}
        <div style={{
          width: 40, height: 2, background: `linear-gradient(90deg, transparent, ${GOLD_L}, transparent)`,
          margin: '20px auto 0',
        }} />
      </div>
    </div>
  );
}
